import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import type { ClientBrandingDto, OAuthClientDto } from "@platform/core";
import { Alert, Button, Checkbox, Field, Input, isHexColor, useToast } from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { BrandPreviewCard } from "../../lib/brand-preview";
import { errorText } from "../../lib/errors";
import { cleanHeroText, heroPlaceholder, HeroTextFields, type HeroText } from "../../lib/hero-text";

/** Per-application look of the sign-in pages (docs/branding.md); empty fields use Settings → Branding. */
export function BrandingTab({ client }: { client: OAuthClientDto }) {
  const { t } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const global = useQuery({ queryKey: ["branding"], queryFn: api.publicBranding, staleTime: 5 * 60_000 });
  const [ownColor, setOwnColor] = useState(Boolean(client.branding?.primaryColor));
  const [color, setColor] = useState(client.branding?.primaryColor ?? global.data?.primaryColor ?? "#009be5");
  const [logoUrl, setLogoUrl] = useState(client.branding?.logoUrl ?? "");
  const [hero, setHero] = useState<HeroText>(() => ({
    ...(client.branding?.heroTitle ? { heroTitle: client.branding.heroTitle } : {}),
    ...(client.branding?.heroBody ? { heroBody: client.branding.heroBody } : {}),
  }));
  const editable = can("clients:write");
  const validColor = isHexColor(color);

  const save = useMutation({
    mutationFn: () => {
      const b: ClientBrandingDto = {
        ...(ownColor ? { primaryColor: color.toLowerCase() } : {}),
        ...(logoUrl.trim() ? { logoUrl: logoUrl.trim() } : {}),
        ...cleanHeroText(hero),
      };
      return api.clients.setBranding(client.clientId, Object.keys(b).length ? b : null);
    },
    onSuccess: (c) => {
      qc.setQueryData(["client", client.clientId], c);
      void qc.invalidateQueries({ queryKey: ["clients"] });
      toast({ title: t("clients.saved"), tone: "success" });
    },
  });
  const fieldErrors = isApiError(save.error) ? save.error.fieldErrors() : {};
  const effectiveColor = ownColor && validColor ? color : (global.data?.primaryColor ?? "#009be5");
  const previewUrl = `${config.authServerUrl}/sign-in?client_id=${encodeURIComponent(client.clientId)}`;

  return (
    <div className="grid gap-6 px-6 py-5 lg:grid-cols-[1fr_320px]">
      <fieldset disabled={!editable} className="min-w-0">
        <p className="mb-4 text-sm text-ink-2">{t("clientBranding.hint")}</p>
        {save.isError && (
          <Alert tone="error" className="mb-4">
            {errorText(save.error)}
          </Alert>
        )}
        <div className="mb-4">
          <Checkbox
            label={t("clientBranding.ownColor")}
            checked={ownColor}
            onChange={(e) => setOwnColor(e.target.checked)}
          />
        </div>
        {ownColor && (
          <Field
            label={t("branding.primaryColor")}
            error={validColor ? fieldErrors["branding.primaryColor"] : "#rrggbb"}
          >
            {(p) => (
              <div className="flex gap-2">
                <input
                  type="color"
                  aria-label={t("branding.primaryColor")}
                  value={validColor ? color : "#009be5"}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-12 cursor-pointer rounded border border-line"
                />
                <Input
                  {...p}
                  data-testid="branding-color-input"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
              </div>
            )}
          </Field>
        )}
        <Field
          label={t("branding.logoUrl")}
          hint={t("clientBranding.logoHint")}
          error={fieldErrors["branding.logoUrl"]}
        >
          {(p) => (
            <Input
              {...p}
              type="url"
              placeholder="https://"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
          )}
        </Field>
        <h3 className="mt-2 mb-1 text-sm font-medium">{t("heroText.section")}</h3>
        <p className="mb-4 text-sm text-ink-2">{t("heroText.appHint")}</p>
        <HeroTextFields
          value={hero}
          onChange={setHero}
          placeholder={heroPlaceholder(global.data?.productName ?? "", global.data)}
          errors={Object.fromEntries(
            Object.entries(fieldErrors).map(([k, v]) => [k.replace(/^branding\./, ""), v]),
          )}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary-dark hover:underline"
          >
            {t("clientBranding.open")}
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
          {editable && (
            <Button
              data-testid="save-branding-button"
              loading={save.isPending}
              disabled={ownColor && !validColor}
              onClick={() => save.mutate()}
            >
              {t("common.save")}
            </Button>
          )}
        </div>
      </fieldset>
      <div>
        <p className="mb-2 text-sm font-medium">{t("branding.preview")}</p>
        <BrandPreviewCard
          name={client.name}
          color={effectiveColor}
          {...(logoUrl.trim() || global.data?.logoUrl
            ? { logoUrl: logoUrl.trim() || global.data?.logoUrl }
            : {})}
        />
      </div>
    </div>
  );
}
